/*
 *  AbstractFirebaseController__AdPreview.js
 *
 *
 *
 */
//==============================================================================
// Internal dependencies
//==============================================================================
const { getLeadFormLink } = require("./Helpers");
//==============================================================================
// External dependencies
//==============================================================================
const _ = require("lodash");

function enrichSnapshotWithId(querySnapshot) {
  const data = [];
  querySnapshot.forEach((doc) => {
    const elem = doc.data();
    elem.id = doc.id;
    data.push(elem);
  });
  return data;
}

const extractSnapshotData = (snap) => {
  return {
    id: snap.id,
    ...snap.data()
  };
};

//==============================================================================
// Module
//==============================================================================
module.exports = function (firebase, logger) {
  return {
    getNewAdPreviewData(campaignId, channel, stage = "released") {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "=", campaignId)
          .where("channel", "=", channel)
          .where("type", "=", stage)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },
    saveAdCreative(data) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives").add(data).then(fullfil).catch(reject);
      });
    },
    updateAdCreative(adCreativeId, data) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .doc(adCreativeId)
          .set(data, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    },
    async saveAdCreatives(creatives = []) {
      const creativesWithoutId = creatives.map((creative) => {
        const { id, ...rest } = creative;
        return rest;
      });

      const batch = firebase.firestore().batch();
      const newCreatives = [];
      creativesWithoutId.forEach((creative) => {
        const creativeRef = this.collection("adcreatives").doc();
        batch.set(creativeRef, creative);
        newCreatives.push({ ...creative, id: creativeRef.id });
      });
      await batch.commit();
      return newCreatives;
    },
    updateAdCreatives(creatives = []) {
      const batch = firebase.firestore().batch();
      creatives.forEach((creative) => {
        const { id } = creative;
        delete creative.id;
        const creativeRef = this.collection("adcreatives").doc(id);
        batch.set(creativeRef, creative, { merge: true });
      });
      return batch.commit();
    },
    deleteAdCreatives(creativeIds = []) {
      const batch = firebase.firestore().batch();
      creativeIds.forEach((creativeId) => {
        const creativeRef = this.collection("adcreatives").doc(creativeId);
        batch.delete(creativeRef);
      });
      return batch.commit();
    },
    async deleteAdCreativesByCampaignId(campaignId) {
      const batch = firebase.firestore().batch();
      const creatives = await this.collection("adcreatives")
        .where("campaignId", "==", campaignId)
        .get();
      creatives.forEach((doc) => {
        batch.delete(doc.ref);
      });
      return batch.commit();
    },
    async deleteAdCreativesByCampaignIdAndOrgaId(campaignId, orgaId) {
      const batch = firebase.firestore().batch();
      const creatives = await this.collection("adcreatives")
        .where("campaignId", "==", campaignId)
        .where("orgaId", "==", orgaId)
        .get();
      creatives.forEach((doc) => {
        batch.delete(doc.ref);
      });
      return batch.commit();
    },
    async getAdCreativeById(adCreativeId) {
      return await this.collection("adcreatives").doc(adCreativeId).get().then(extractSnapshotData);
    },

    getAdCreativesByCampaignIdAndOrgaId(campaignId, orgaId) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "==", campaignId)
          .where("orgaId", "==", orgaId)
          .get()
          .then((query) => {
            const enrichedData = enrichSnapshotWithId(query);
            fullfil(enrichedData);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    getAdCreativesByCampaignId(campaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "==", campaignId)
          .get()
          .then((query) => {
            const enrichedData = enrichSnapshotWithId(query);
            fullfil(enrichedData);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    getAdCreativesByCampaignIdAndPost({ campaignId, postNumber }) {
      return new Promise((fulfill, reject) => {
        let query = this.collection("adcreatives").where("campaignId", "==", campaignId);

        if (postNumber) {
          query = query.where("postNumber", "==", postNumber);
        }

        query
          .get()
          .then((querySnapshot) => fulfill(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    },

    findArchivedAdCreativesByCampaignId({ campaignId }) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives_archived")
          .where("campaignId", "==", campaignId)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },

    getAdCreativesByCampaignIdAndChannel(campaignId, channel) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "==", campaignId)
          .where("channel", "==", channel)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },

    getAdCreativesByTitleAndCompany(cleanTitle, companyName, orgaId) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("channelData.cleanJobTitle", "==", cleanTitle)
          .where("channelData.companyName", "==", companyName)
          .where("orgaId", "==", orgaId)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },

    getAdCreativesByCompany(companyName, orgaId) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("channelData.companyName", "==", companyName)
          .where("orgaId", "==", orgaId)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },

    getLastLinkedInAdCreativesByCompanyAndOrgaId(companyName, orgaId) {
      return new Promise((fulfil, reject) => {
        this.collection("adcreatives")
          .where("channelData.companyName", "==", companyName)
          .where("orgaId", "==", orgaId)
          .where("channel", "==", "linkedin")
          .orderBy("channelData.linkedInCompanyId")
          .orderBy("createdDate", "desc")
          .get()
          .then((query) => fulfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },

    getAdCreativesByOrga(orgaId) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("orgaId", "==", orgaId)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },
    getAllAdCreatives() {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },
    getAdCreativesByTitleAndChannels(cleanTitle, channels) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("channelData.cleanJobTitle", "==", cleanTitle)
          .where("channel", "in", channels)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },
    async copyAdCreatives({
      sourceCampaignId,
      targetCampaignId,
      campaignGoal,
      channelCampaignIds = [],
      adsetId,
      jobCategoryId,
      orgaId
    }) {
      logger.log("copyAdCreatives", "Copy ad previews data", {
        sourceCampaignId,
        targetCampaignId,
        campaignGoal,
        channelCampaignIds,
        adsetId,
        jobCategoryId,
        orgaId
      });

      try {
        if (!sourceCampaignId || !targetCampaignId) {
          return logger.log("error:copyAdCreatives", "No source or target campaign id set", {
            sourceCampaignId,
            targetCampaignId,
            campaignGoal,
            adsetId
          });
        }

        if (!adsetId) {
          return logger.log("error:copyAdCreatives", "No adsetId passed in", {
            sourceCampaignId,
            targetCampaignId,
            campaignGoal,
            adsetId
          });
        }
        logger.log("copyAdCreatives", "Is BewerberHub campaign?", {
          sourceCampaignId,
          targetCampaignId,
          isBewerberHub: campaignGoal
        });
        // Find the original adcreatives for the given campaign
        let adPreviewData = await this.collection("adcreatives")
          .where("campaignId", "==", sourceCampaignId)
          .where("orgaId", "==", orgaId)
          .get()
          .then((query) => enrichSnapshotWithId(query));

        logger.log("copyAdCreatives", "ad preveiw data from source campaign id", {
          sourceCampaignId,
          adPreviewData
        });

        if (!adPreviewData?.length) {
          logger.log("error:copyAdCreatives", "The source campaign didn't have any ad previews", {
            sourceCampaignId,
            targetCampaignId,
            orgaId,
            campaignGoal
          });
          return [];
        }

        logger.log("copyAdCreatives", "Reset all the old values of from the source id", {
          sourceCampaignId,
          targetCampaignId,
          campaignGoal,
          orgaId
        });
        const orga = await this.collection("organizations").doc(orgaId).get();

        logger.debug("copyAdCreatives", "Orga data", {
          orga: orga.data()
        });

        // Reset value from the copied ad preview
        adPreviewData = adPreviewData.map((adCreative) => {
          adCreative.campaignId = targetCampaignId;
          adCreative.adsetId = adsetId; // TODO: we need the channel campaign Id
          adCreative.channelCampaignIds = channelCampaignIds; // TODO: we need the channel campaign Id
          // Reset all the other values
          adCreative.createdBy = "";
          adCreative.releasedVersion = 1;
          adCreative.stagingVersion = 0;
          adCreative.createdDate = new Date().toISOString();
          adCreative.updatedDate = new Date().toISOString();
          adCreative.lastUpdatedBy = new Date().toISOString();

          // NOTE: Only for BewerberHub campaigns
          // ----------------------------------
          // This following logic is there to deal with the application link
          // for bewerberHub campaigns. Since those are using the campaignId and other
          // values in the URL
          //
          if (adCreative?.channelData?.url && campaignGoal == "applicants") {
            const { subdomain } = orga.data();

            // use helper function to
            const newBewerberHubLink = getLeadFormLink({
              jobCategoryId,
              campaignId: targetCampaignId,
              subdomain,
              channel: adCreative?.channel
            });

            logger.log("copyAdCreatives", "Reset job form url for bewerberHub campaign", {
              targetCampaignId,
              orgaId,
              jobCategoryId,
              subdomain,
              channel: adCreative?.channel,
              newBewerberHubLink
            });

            adCreative.channelData.url = newBewerberHubLink;
          }
          return adCreative;
        });

        logger.log("copyAdCreatives", "Fixed ad preview data", adPreviewData);

        let stagingAdPreviews = adPreviewData.filter((adPreview) => adPreview.type === "staging");
        // IMPORTANT NOTE:
        // ------------------------
        // this following logic, was created to deal with old ad preview, that didnt use the new structure and
        // were never released. Its a workaround for those older campaigns.
        if (!stagingAdPreviews.length) {
          // Deep clone
          stagingAdPreviews = _.cloneDeep(adPreviewData);
          // Make sure we only store staging ad previews
          stagingAdPreviews = _.forEach(stagingAdPreviews, (adPreview) => {
            adPreview.type = "staging";
          });
          adPreviewData = stagingAdPreviews.concat(adPreviewData);
        }

        logger.log("copyAdCreatives", "Save adpreview data", adPreviewData);

        return await this.saveAdCreatives(adPreviewData);
      } catch (error) {
        logger.log("error:copyAdCreatives", "Error while copying ad previews", {
          error,
          sourceCampaignId,
          targetCampaignId,
          campaignGoal,
          channelCampaignIds,
          adsetId,
          jobCategoryId,
          orgaId
        });

        throw new Error(error);
      }
    },

    async getLastestAdCreativeByCampaignAndChannel(campaignId, channel) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "==", campaignId)
          .where("channel", "==", channel)
          .where("type", "==", "released")
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    },
    async getAdCreativesByCampaignAndType(campaignId, type) {
      return new Promise((fullfil, reject) => {
        this.collection("adcreatives")
          .where("campaignId", "==", campaignId)
          .where("type", "==", type)
          .get()
          .then((query) => fullfil(enrichSnapshotWithId(query)))
          .catch(reject);
      });
    }
  };
};
